import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type {
  CategoriaDocumento,
  ChecklistDocumentos,
  DocumentoApi,
  DocumentoVinculado,
  SolicitudDetalle,
} from "@/lib/types";

interface SubirArgs {
  numeroCfdi: number;
  categoria: CategoriaDocumento;
  file: File;
  /** Tipo del catálogo (P31) · obligatorio cuando categoria === "Adicional". */
  tipoDocumento?: string;
}

interface SubirMutationContext {
  previous: SolicitudDetalle | null;
  tempId: string | null;
}

interface EliminarMutationContext {
  previous: SolicitudDetalle | null;
}

/**
 * Mutación de subida con **optimistic update + setQueryData**.
 *
 * En vez de invalidar `qk.solicitud(id)` (que refetcha TODO el detalle ·
 * cfdis, prorrateo, flujo, eventos, chat, documentos), actualizamos el
 * cache directamente con la respuesta del backend. El usuario ve el
 * documento al instante y no pagamos un segundo roundtrip HTTP.
 *
 * - `onMutate`: agrega un `DocumentoVinculado` con id temporal al checklist.
 * - `onSuccess`: reemplaza el temporal por el real (sin refetch HTTP).
 * - `onError`: rollback al estado pre-optimista.
 */
export function useSubirDocumento(
  solicitudId: string,
  categoriaLabel: string,
  options?: {
    /** Recibe el DocumentoVinculado REAL · el caller lo trackea como "en despacho". */
    onSubido?: (doc: DocumentoVinculado) => void;
  }
) {
  const qc = useQueryClient();
  return useMutation<DocumentoApi, Error, SubirArgs, SubirMutationContext>({
    mutationFn: ({ numeroCfdi, categoria, file, tipoDocumento }) =>
      documentosApi.subir(solicitudId, numeroCfdi, categoria, file, tipoDocumento),

    onMutate: async ({ categoria, file, tipoDocumento }) => {
      const queryKey = qk.solicitud(solicitudId);
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SolicitudDetalle>(queryKey) ?? null;
      if (!previous) return { previous: null, tempId: null };

      const tempId = `temp-${crypto.randomUUID()}`;
      const optimistic: DocumentoVinculado = {
        id: tempId,
        documentoId: tempId,
        categoria,
        nombreOriginal: file.name,
        vinculadoAt: new Date().toISOString(),
        tipoDocumento: tipoDocumento ?? null,
      };
      qc.setQueryData<SolicitudDetalle>(
        queryKey,
        addDocumentoAlDetalle(previous, optimistic)
      );
      return { previous, tempId };
    },

    onSuccess: (doc, _vars, context) => {
      const queryKey = qk.solicitud(solicitudId);
      const current = qc.getQueryData<SolicitudDetalle>(queryKey);
      const real: DocumentoVinculado = {
        id: doc.id,
        documentoId: doc.id,
        categoria: doc.categoria,
        nombreOriginal: doc.nombreOriginal,
        vinculadoAt: doc.cargadoAt,
        tipoDocumento: doc.tipoDocumento ?? null,
      };
      if (current && context?.tempId) {
        qc.setQueryData<SolicitudDetalle>(
          queryKey,
          reemplazarDocumentoEnDetalle(current, context.tempId, real)
        );
      }
      toastExito({ title: `${categoriaLabel} subido`, message: doc.nombreOriginal });
      options?.onSubido?.(real);
    },

    onError: (e, _vars, context) => {
      rollback(qc, solicitudId, context?.previous ?? null);
      toastErrorApi(e);
    },
  });
}

/**
 * Mutación de eliminación con optimistic update.
 * Mismo patrón que subir · removemos del cache al instante y reconciliamos
 * (o hacemos rollback en error).
 */
export function useEliminarDocumento(solicitudId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string, EliminarMutationContext>({
    mutationFn: (documentoId) => documentosApi.eliminar(solicitudId, documentoId),

    onMutate: async (documentoId) => {
      const queryKey = qk.solicitud(solicitudId);
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SolicitudDetalle>(queryKey) ?? null;
      if (!previous) return { previous: null };
      qc.setQueryData<SolicitudDetalle>(
        queryKey,
        removerDocumentoDelDetalle(previous, documentoId)
      );
      return { previous };
    },

    onSuccess: () => {
      toastExito({ title: "Documento eliminado", message: "Se eliminó el archivo." });
    },

    onError: (e, _vars, context) => {
      rollback(qc, solicitudId, context?.previous ?? null);
      toastErrorApi(e);
    },
  });
}

export function useDescargarDocumento(solicitudId: string) {
  return useMutation({
    mutationFn: ({ id, nombre }: { id: string; nombre: string }) => documentosApi.descargar(solicitudId, id, nombre),
    onError: toastErrorApi,
  });
}

// === Helpers puros · recálculo del checklist client-side ====================

/**
 * Re-inyecta al detalle los documentos que el SERVIDOR aún no refleja (el
 * outbox que vincula el documento al checklist tarda unos segundos) · evita
 * que un refetch "borre" visualmente un archivo recién subido. Idempotente:
 * los que ya están presentes no se duplican.
 */
export function mergeDocumentosEnDetalle(
  detalle: SolicitudDetalle,
  docs: DocumentoVinculado[]
): SolicitudDetalle {
  const presentes = new Set(detalle.checklist.documentos.map((d) => d.documentoId));
  const faltantes = docs.filter((d) => !presentes.has(d.documentoId));
  if (faltantes.length === 0) return detalle;
  const documentos = [...detalle.checklist.documentos, ...faltantes];
  return { ...detalle, checklist: recalcChecklist(detalle.checklist, documentos) };
}

function addDocumentoAlDetalle(
  detalle: SolicitudDetalle,
  doc: DocumentoVinculado
): SolicitudDetalle {
  const documentos = [...detalle.checklist.documentos, doc];
  return { ...detalle, checklist: recalcChecklist(detalle.checklist, documentos) };
}

function reemplazarDocumentoEnDetalle(
  detalle: SolicitudDetalle,
  tempId: string,
  real: DocumentoVinculado
): SolicitudDetalle {
  const documentos = detalle.checklist.documentos.map((d) =>
    d.id === tempId ? real : d
  );
  return { ...detalle, checklist: recalcChecklist(detalle.checklist, documentos) };
}

function removerDocumentoDelDetalle(
  detalle: SolicitudDetalle,
  documentoId: string
): SolicitudDetalle {
  const documentos = detalle.checklist.documentos.filter(
    (d) => d.id !== documentoId && d.documentoId !== documentoId
  );
  return { ...detalle, checklist: recalcChecklist(detalle.checklist, documentos) };
}

/**
 * Recalcula los contadores del checklist a partir de la lista de documentos.
 * Replica la lógica del backend · cada GET del detalle reconcilia, así que
 * un drift temporal es seguro.
 */
function recalcChecklist(
  previo: ChecklistDocumentos,
  documentos: DocumentoVinculado[]
): ChecklistDocumentos {
  const facturaXmlCargados = documentos.filter((d) => d.categoria === "FacturaXml").length;
  const facturaPdfCargados = documentos.filter((d) => d.categoria === "FacturaPdf").length;
  const adicionalesCargados = documentos.filter((d) => d.categoria === "Adicional").length;
  const otrosCargados = documentos.filter(
    (d) => d.categoria === "Evidencia" || d.categoria === "Contrato"
  ).length;
  return {
    ...previo,
    facturaXmlCargados,
    facturaPdfCargados,
    adicionalesCargados,
    otrosCargados,
    completoFB60:
      facturaXmlCargados >= 1 && facturaPdfCargados >= 1 && adicionalesCargados >= 1,
    documentos,
  };
}

function rollback(
  qc: QueryClient,
  solicitudId: string,
  previous: SolicitudDetalle | null
) {
  if (previous) qc.setQueryData(qk.solicitud(solicitudId), previous);
}
